import {
  useEffect,
  useId,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { ChevronDown, ChevronRight, Info } from "lucide-react";
import SecondaryButton from "@/components/SecondaryButton";
import { MASTERY_CATEGORIES } from "../service/mastery";
import { MasteryLevel } from "../types/enums";
import { summaryRootTarget } from "./model";
import { summaryTargetKey } from "./navigation";
import SummaryBar, { SummaryMetrics, SummaryRowStats } from "./SummaryBar";
import SummaryTooltip, { type SummaryEventHandler } from "./SummaryTooltip";
import { SUMMARY_LEVELS, summaryDisplayStats } from "./presentation";
import type {
  SummaryContext,
  SummaryDomain,
  SummaryDomainGrade,
  SummaryRow,
  SummaryStandard,
  SummaryModel,
  SummaryTarget,
} from "./types";
import "./summary.css";
import "./standards-view.css";

export type StandardsSummaryProps = {
  context: SummaryContext;
  model: SummaryModel;
  onNavigate: (target: SummaryTarget) => void;
  onEvent?: SummaryEventHandler;
  headerActions?: ReactNode;
};

const masteryColors = Object.fromEntries(
  SUMMARY_LEVELS.flatMap((status) => [
    [`--et-summary-${status}-dot`, MASTERY_CATEGORIES[status].dot],
    [`--et-summary-${status}-pill`, MASTERY_CATEGORIES[status].pill],
  ]),
) as CSSProperties;

const legendDescriptions: Record<MasteryLevel, string> = {
  [MasteryLevel.MASTERED]:
    "Mastered mastery, as shown in the heatmap. In student bars, the color summarizes started standards using their mastery points.",
  [MasteryLevel.PROFICIENT]:
    "Proficient mastery, as shown in the heatmap. Proficient and Mastered both count toward at least proficient.",
  [MasteryLevel.APPROACHING]:
    "Approaching mastery, as shown in the heatmap. These students or standards have started and are working toward proficiency.",
  [MasteryLevel.NOT_STARTED]: `${MASTERY_CATEGORIES[MasteryLevel.NOT_STARTED].label} means there is no mastery result to show yet. Standard results on a Student page require that student's own direct exit-ticket evidence.`,
};

type CommonProps = { level: string; onEvent?: SummaryEventHandler };

function Legend({ level, onEvent }: CommonProps) {
  return (
    <div className="et-summary-legend" aria-label="Mastery legend">
      {SUMMARY_LEVELS.map((status) => (
        <SummaryTooltip
          key={status}
          title={MASTERY_CATEGORIES[status].label}
          lines={[legendDescriptions[status]]}
          className="et-summary-legend-item"
          onOpen={() =>
            onEvent?.("hover_opened", { element: "legend", level, status })
          }
        >
          <span
            className={`et-summary-swatch et-summary-segment-${status}${status === MasteryLevel.NOT_STARTED ? " et-standards-not-started" : ""}`}
            aria-hidden="true"
          />
          <span>{MASTERY_CATEGORIES[status].label}</span>
        </SummaryTooltip>
      ))}
      <SummaryTooltip
        title="The same standard mastery"
        lines={[
          "Only standards with a student's own direct exit-ticket evidence contribute to Summary calculations. Those standards retain their full mastery score.",
        ]}
        label="How Summary mastery colors work"
        className="et-summary-info"
        onOpen={() =>
          onEvent?.("hover_opened", { element: "legend_info", level })
        }
      >
        <Info size={16} aria-hidden="true" />
      </SummaryTooltip>
    </div>
  );
}

function StandardRows({
  standards,
  level,
  onEvent,
}: { standards: SummaryStandard[] } & CommonProps) {
  return (
    <div className="et-summary-standards">
      {standards.map((standard) => (
        <div
          key={standard.id}
          className={`et-summary-standard-row${standard.status ? " et-summary-standard-student" : " et-summary-row"}`}
        >
          <div className="et-summary-row-name et-summary-standard-name">
            <SummaryTooltip
              title={standard.id}
              lines={[
                standard.description,
                standard.status
                  ? `${MASTERY_CATEGORIES[standard.status].label}. ${standard.stats.hasDirectEvidence === false ? "This student has no direct exit-ticket evidence on this standard yet. It is N/A and excluded from overall and domain calculations." : "Its status is the same as the heatmap."}`
                  : "Each student’s color uses their full mastery score when they have direct exit-ticket evidence; otherwise N/A. This standard appears once a student in the current scope has direct evidence on it.",
              ]}
              className="et-summary-standard-code"
              onOpen={() =>
                onEvent?.("hover_opened", {
                  element: "standard",
                  level,
                  standard: standard.id,
                })
              }
            >
              {standard.id}
            </SummaryTooltip>
            <p>{standard.description}</p>
          </div>
          {standard.status ? (
            <SummaryTooltip
              title={`${standard.id}: ${MASTERY_CATEGORIES[standard.status].label}`}
              lines={[
                standard.stats.hasDirectEvidence === false
                  ? "This student has no direct exit-ticket evidence on this standard yet. It is N/A and excluded from overall and domain calculations."
                  : "This status comes straight from the heatmap. There is no averaging or additional score.",
              ]}
              className={`et-summary-status et-summary-status-${standard.status}${standard.status === MasteryLevel.NOT_STARTED ? " et-standards-not-started" : ""}`}
              onOpen={() =>
                onEvent?.("hover_opened", {
                  element: "student_standard_status",
                  level,
                  standard: standard.id,
                })
              }
            >
              {MASTERY_CATEGORIES[standard.status].label}
            </SummaryTooltip>
          ) : (
            <SummaryRowStats
              stats={standard.stats}
              name={standard.id}
              level={level}
              basis="standard"
              onEvent={onEvent}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function DomainGrade({
  grade,
  domain,
  open,
  onToggle,
  level,
  onEvent,
}: {
  grade: SummaryDomainGrade;
  domain: SummaryDomain;
  open: boolean;
  onToggle: () => void;
} & CommonProps) {
  const id = useId();
  return (
    <div className="et-summary-domain-grade">
      <div className="et-summary-row">
        <div className="et-summary-row-name">
          <button
            type="button"
            className="et-summary-accordion-label"
            aria-expanded={open}
            aria-controls={id}
            onClick={onToggle}
          >
            {open ? (
              <ChevronDown size={17} aria-hidden="true" />
            ) : (
              <ChevronRight size={17} aria-hidden="true" />
            )}
            <span>{grade.name}</span>
          </button>
          <SummaryTooltip
            title={`${domain.name}: ${grade.name}`}
            lines={[
              `${grade.stats.total} students enrolled in ${grade.name} in this scope.`,
              "Each student’s domain color averages points only on the domain’s standards with their own direct exit-ticket evidence.",
            ]}
            className="et-summary-detail et-summary-indented-detail"
            onOpen={() =>
              onEvent?.("hover_opened", {
                element: "domain_grade",
                level,
                domain: domain.id,
                grade: grade.grade,
              })
            }
          >
            {grade.stats.total} students
          </SummaryTooltip>
        </div>
        <SummaryRowStats
          stats={grade.stats}
          name={`${domain.name}, ${grade.name}`}
          level={level}
          basis="domain"
          onEvent={onEvent}
        />
      </div>
      {open && (
        <div id={id}>
          <StandardRows
            standards={grade.standards}
            level={level}
            onEvent={onEvent}
          />
        </div>
      )}
    </div>
  );
}

function DomainRow({
  domain,
  open,
  expanded,
  onToggle,
  toggleGrade,
  level,
  onEvent,
}: {
  domain: SummaryDomain;
  open: boolean;
  expanded: Set<string>;
  onToggle: () => void;
  toggleGrade: (
    key: string,
    domain: SummaryDomain,
    grade: SummaryDomainGrade,
  ) => void;
} & CommonProps) {
  const id = useId();
  const studentStandards = domain.stats.unit === "standards";
  return (
    <div className="et-summary-domain">
      <div className="et-summary-row">
        <div className="et-summary-row-name">
          <button
            type="button"
            className="et-summary-accordion-label"
            aria-expanded={open}
            aria-controls={id}
            onClick={onToggle}
          >
            {open ? (
              <ChevronDown size={18} aria-hidden="true" />
            ) : (
              <ChevronRight size={18} aria-hidden="true" />
            )}
            <span>
              {domain.name}
              {domain.category && <small>{domain.category}</small>}
            </span>
          </button>
          <SummaryTooltip
            title={`${domain.name}: what this bar counts`}
            lines={
              studentStandards
                ? [
                    `${domain.stats.total} enrolled-grade standards in this domain.`,
                    `Each standard uses this student's full mastery score when they have direct exit-ticket evidence; otherwise ${MASTERY_CATEGORIES[MasteryLevel.NOT_STARTED].label}.`,
                  ]
                : [
                    `${domain.stats.total} students whose enrolled grade has standards in this domain.`,
                    "Students in a grade without this domain are left out, so this count can be smaller than the top card.",
                    "Each student’s domain color averages points on the domain’s standards with their own direct exit-ticket evidence.",
                  ]
            }
            className="et-summary-detail et-summary-indented-detail"
            onOpen={() =>
              onEvent?.("hover_opened", {
                element: "domain_count",
                level,
                domain: domain.id,
              })
            }
          >
            {domain.stats.total} {domain.stats.unit}
          </SummaryTooltip>
        </div>
        <SummaryRowStats
          stats={domain.stats}
          name={domain.name}
          level={level}
          basis="domain"
          onEvent={onEvent}
        />
      </div>
      {open && (
        <div id={id} className="et-summary-domain-contents">
          {domain.grades.length > 0 ? (
            domain.grades.map((grade) => {
              const key = `${domain.id}:${grade.grade}`;
              return (
                <DomainGrade
                  key={key}
                  grade={grade}
                  domain={domain}
                  open={expanded.has(key)}
                  onToggle={() => toggleGrade(key, domain, grade)}
                  level={level}
                  onEvent={onEvent}
                />
              );
            })
          ) : (
            <StandardRows
              standards={domain.standards}
              level={level}
              onEvent={onEvent}
            />
          )}
        </div>
      )}
    </div>
  );
}

function NextLevelRow({
  row,
  nested = false,
  parentName,
  onNavigate,
  level,
  onEvent,
}: {
  row: SummaryRow;
  nested?: boolean;
  parentName?: string;
  onNavigate: (target: SummaryTarget) => void;
} & CommonProps) {
  const tooltipName = parentName ? `${parentName}: ${row.name}` : row.name;
  const displayStats = summaryDisplayStats(row.stats);
  return (
    <>
      <div
        className={`et-summary-row et-summary-next-row${nested ? " et-summary-nested-row" : ""}`}
        onClick={() => onNavigate(row.target)}
      >
        <div className="et-summary-row-name">
          <button
            type="button"
            className="et-summary-navigation-label"
            onClick={(event) => {
              event.stopPropagation();
              onNavigate(row.target);
            }}
          >
            <span>{row.name}</span>
          </button>
          <SummaryTooltip
            title={tooltipName}
            lines={[
              parentName
                ? `${parentName}, ${row.name}: ${row.detail}.`
                : row.detail,
              `${SUMMARY_LEVELS.map((status) => `${displayStats.counts[status]} ${MASTERY_CATEGORIES[status].label}`).join(", ")}.`,
              displayStats.hasDirectEvidence === false
                ? "Results appear once direct exit-ticket evidence is available in this scope."
                : row.stats.unit === "standards"
                  ? "The bar counts this student’s enrolled-grade standards, using their heatmap status."
                  : "Each student’s overall color averages points on enrolled-grade standards with their own direct exit-ticket evidence.",
            ]}
            className="et-summary-detail"
            onOpen={() =>
              onEvent?.("hover_opened", { element: `${row.kind}_row`, level })
            }
          >
            {row.detail}
          </SummaryTooltip>
        </div>
        <SummaryRowStats
          stats={row.stats}
          name={tooltipName}
          level={level}
          basis="overall"
          onEvent={onEvent}
        />
      </div>
      {row.children.map((child) => (
        <NextLevelRow
          key={child.id}
          row={child}
          nested
          parentName={row.name}
          onNavigate={onNavigate}
          level={level}
          onEvent={onEvent}
        />
      ))}
    </>
  );
}

export default function StandardsSummary({
  context,
  model: view,
  onNavigate,
  onEvent,
  headerActions,
}: StandardsSummaryProps) {
  const targetKey = view.denied ? "denied" : summaryTargetKey(view.target);
  const studentPage = !view.denied && view.target.level === "student";
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(studentPage ? ["domains"] : []),
  );
  const domainSectionId = useId();
  useEffect(
    () => setExpanded(new Set(studentPage ? ["domains"] : [])),
    [targetKey, studentPage],
  );
  const toggle = (key: string, onOpen?: () => void) => {
    if (!expanded.has(key)) onOpen?.();
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (view.denied) {
    return (
      <div className="et-summary-denied" role="alert">
        <p>You don’t have access to this page.</p>
        <SecondaryButton
          size="sm"
          aria-label="Return to your Summary"
          onClick={() => onNavigate(summaryRootTarget(context))}
        >
          Return to your Summary
        </SecondaryButton>
      </div>
    );
  }

  const target = view.target;
  const domainsOpen = expanded.has("domains");
  return (
    <div className="et-standards-summary" style={masteryColors}>
      <nav
        className="et-summary-breadcrumbs"
        aria-label="Standards Summary breadcrumb"
      >
        <ol>
          {view.breadcrumbs.map((breadcrumb, index) => (
            <li key={`${index}:${breadcrumb.label}`}>
              {index > 0 && <ChevronRight size={14} aria-hidden="true" />}
              {index === view.breadcrumbs.length - 1 ? (
                <span aria-current="page">{breadcrumb.label}</span>
              ) : (
                <button
                  type="button"
                  onClick={() => onNavigate(breadcrumb.target)}
                >
                  {breadcrumb.label}
                </button>
              )}
            </li>
          ))}
        </ol>
      </nav>
      <section
        className="et-summary-top-card"
        aria-label={`${view.title} mastery summary`}
      >
        <div className="et-summary-top-heading">
          <div>
            <p className="et-summary-level-label">{view.levelLabel}</p>
            <h2>{view.title}</h2>
            <SummaryTooltip
              title={`${view.title}: scope`}
              lines={[
                view.detail,
                view.stats.unit === "standards"
                  ? `${view.stats.total} standards at this student’s enrolled grade. The bar includes standards that are ${MASTERY_CATEGORIES[MasteryLevel.NOT_STARTED].label}.`
                  : `${view.stats.total} distinct students with an enrolled grade. Each is measured only on their own enrolled-grade standards.`,
              ]}
              className="et-summary-context"
              onOpen={() =>
                onEvent?.("hover_opened", {
                  element: "scope_count",
                  level: target.level,
                })
              }
            >
              {view.detail}
            </SummaryTooltip>
          </div>
          {headerActions}
        </div>
        <div className="et-summary-top-data">
          <SummaryMetrics
            stats={view.stats}
            name={view.title}
            level={target.level}
            onEvent={onEvent}
          />
          <SummaryBar
            stats={view.stats}
            name={view.title}
            level={target.level}
            basis="overall"
            onEvent={onEvent}
          />
        </div>
        <Legend level={target.level} onEvent={onEvent} />
      </section>
      {context.viewer.role === "ORG_ADMIN" &&
        view.missingGradeStudents.length > 0 && (
          <div className="et-summary-warning" role="status">
            <strong>
              {view.missingGradeStudents.length}{" "}
              {view.missingGradeStudents.length === 1
                ? "student is"
                : "students are"}{" "}
              missing an enrolled grade.
            </strong>
            <p>
              They are left out of Summary calculations until a grade is set:{" "}
              {view.missingGradeStudents
                .map((student) => student.name)
                .join(", ")}
              .
            </p>
          </div>
        )}
      <section className="et-summary-card" aria-label="Domain">
        <div className="et-summary-section-heading">
          <h3>
            <button
              type="button"
              className="et-summary-section-toggle"
              aria-expanded={domainsOpen}
              aria-controls={domainSectionId}
              onClick={() =>
                toggle("domains", () =>
                  onEvent?.("domain_opened", {
                    level: target.level,
                    domain: "section",
                  }),
                )
              }
            >
              {domainsOpen ? (
                <ChevronDown size={20} aria-hidden="true" />
              ) : (
                <ChevronRight size={20} aria-hidden="true" />
              )}{" "}
              Domain
            </button>
          </h3>
          <SummaryTooltip
            title="Domain mastery"
            lines={
              target.level === "student"
                ? [
                    `Each bar counts this student’s enrolled-grade standards in the domain, including ${MASTERY_CATEGORIES[MasteryLevel.NOT_STARTED].label}.`,
                  ]
                : [
                    "Each bar counts students whose enrolled grade includes this domain.",
                    "Each student’s domain color averages Mastered, Proficient and Approaching points only on standards with their own direct exit-ticket evidence.",
                  ]
            }
            className="et-summary-info"
            label="How domain mastery is calculated"
            onOpen={() =>
              onEvent?.("hover_opened", {
                element: "domain_info",
                level: target.level,
              })
            }
          >
            <Info size={16} aria-hidden="true" />
          </SummaryTooltip>
        </div>
        {domainsOpen && (
          <div id={domainSectionId}>
            {view.domains.length === 0 ? (
              <p className="et-summary-empty">
                {view.stats.total === 0
                  ? view.stats.unit === "standards"
                    ? "No standards"
                    : "No students at this level"
                  : MASTERY_CATEGORIES[MasteryLevel.NOT_STARTED].label}
              </p>
            ) : (
              view.domains.map((domain) => (
                <DomainRow
                  key={domain.id}
                  domain={domain}
                  open={expanded.has(domain.id)}
                  expanded={expanded}
                  onToggle={() =>
                    toggle(domain.id, () =>
                      onEvent?.("domain_opened", {
                        level: target.level,
                        domain: domain.id,
                      }),
                    )
                  }
                  toggleGrade={(key, selectedDomain, grade) =>
                    toggle(key, () =>
                      onEvent?.("domain_grade_opened", {
                        level: target.level,
                        domain: selectedDomain.id,
                        grade: grade.grade,
                      }),
                    )
                  }
                  level={target.level}
                  onEvent={onEvent}
                />
              ))
            )}
          </div>
        )}
      </section>
      {view.sections.map((section) => (
        <section
          key={section.id}
          className="et-summary-card"
          aria-label={section.title}
        >
          <div className="et-summary-section-heading et-summary-list-heading">
            <h3>{section.title}</h3>
            {section.subtitle && <p>{section.subtitle}</p>}
          </div>
          {section.rows.length === 0 ? (
            <p className="et-summary-empty">
              {section.title === "Groups"
                ? "No groups yet"
                : "No students at this level"}
            </p>
          ) : (
            section.rows.map((row) => (
              <NextLevelRow
                key={row.id}
                row={row}
                onNavigate={onNavigate}
                level={target.level}
                onEvent={onEvent}
              />
            ))
          )}
        </section>
      ))}
    </div>
  );
}
